import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../Components/Sidebar";
import { useNavigate } from "react-router-dom";
import { TbBusinessplan } from "react-icons/tb";
import { PiTextColumnsLight } from "react-icons/pi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { addLoan } from "../../redux/actions/loanAction";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import PageComponent from "../../Components/PageComponent";
import imageCompression from 'browser-image-compression';

function AddLoan() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      ["clean"],
    ],
  };

  const formats = ["font", "header", "list", "bold", "italic", "underline", "align"];

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    eligibilityCriteria: "",
    applicationProcess: "",
    contactInformation: "",
    files: [],
  });

  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuillChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    const compressedFiles = [];

    for (const file of files) {
      if (file.type.startsWith('image/')) {
        try {
          const compressedFile = await imageCompression(file, {
            maxSizeMB: 0.2, // Adjust based on acceptable quality loss
            maxWidthOrHeight: 1200, // Adjust for maximum dimensions
          });
          compressedFiles.push(compressedFile);
        } catch (error) {
          console.error("Image compression error:", error);
          compressedFiles.push(file); // Use original if compression fails
        }
      } else {
        compressedFiles.push(file); // Add non-image files directly
      }
    }

    setFormData((prev) => ({ ...prev, files: [...prev.files, ...compressedFiles] }));
  };

  const handleAddFileField = () => {
    fileInputRef.current.click();
  };

  const handleRemoveFileField = (index) => {
    const updatedFiles = formData.files.filter((_, fileIndex) => fileIndex !== index);
    setFormData((prev) => ({ ...prev, files: updatedFiles }));
  };

   // Strip HTML to validate rich text fields
   const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleSubmit = async (e) => {  //Make handleSubmit async
    e.preventDefault();

    const plainDescription = stripHtml(formData.description);
    const plainEligibilityCriteria = stripHtml(formData.eligibilityCriteria);
    const applicationProcess = stripHtml(formData.applicationProcess);
    const contactInformation = stripHtml(formData.contactInformation);

    // Validate required fields
    if (!formData.title?.trim()) {
      toast.error("Program name is required.");
      return;
    }
    if (!plainDescription.trim()) {
      toast.error("Description is required.");
      return;
    }
    if (!plainEligibilityCriteria.trim()) {
      toast.error("Eligibility criteria is required.");
      return;
    }
    if (!applicationProcess.trim()) {
      toast.error("Application Process are required.");
      return;
    }
    if (!contactInformation.trim()) {
      toast.error("Contact Information are required.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("eligibity_criteria", formData.eligibilityCriteria);
    formDataToSend.append("application_process", formData.applicationProcess);
    formDataToSend.append("contact_information", formData.contactInformation);

    formData.files.forEach((file) => {
      formDataToSend.append("files", file); // Append each file with the same field name
    });

    try {
      await dispatch(addLoan(formDataToSend)); // Await the dispatch

      toast.success("Loan scheme added successfully!");

      // Reset form data
      setFormData({
        title: "",
        description: "",
        eligibilityCriteria: "",
        applicationProcess: "",
        contactInformation: "",
        files: [],
      });
    } catch (error) {
      // Show error message
      toast.error(error.message || "Failed to add loan scheme. Please try again.", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
    }
  };

  return (
    <><PageComponent>
      <div className="flex w-screen h-screen bg-[#F8F7FA] overflow-hidden">
        <div className="fixed top-0 left-0 w-64 h-full bg-white overflow-y-auto  shadow-md">
          <Sidebar />
        </div>
        <div className="flex-1 min-h-screen p-6 ml-64 overflow-auto">
          <div className="flex items-center space-x-2 text-3xl text-gray-500">
            <span>Academics & International</span>
            <span>/</span>
            <span className="text-black"> Interest Free Loan Scheme</span>
          </div>

          <div className="flex items-center mt-4 space-x-4">
            <button
              className="flex items-center px-4 py-2 text-white bg-[#92288C] rounded-lg shadow hover:bg-[#6C0B67]"
              onClick={() => navigate("/academics&international/loanScheme/addLoan")}
            >
              <TbBusinessplan className="mr-2" />
              Add Loan Scheme
            </button>
            <button
              className="flex items-center px-4 py-2 text-[#A8AAAE] rounded-lg shadow hover:bg-gray-100"
              onClick={() => navigate("/academics&international/loanScheme/allLoans")}
            >
              <PiTextColumnsLight className="mr-2" />
              All Loan Schemes
            </button>
          </div>

          <div className="w-full p-6 mt-6 bg-white rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-semibold text-gray-700">Add New Interest Free Loan Scheme</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-600">Title</label>
                  <input
                    name="title"
                    type="text"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-1/2 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block mb-1 text-gray-700">Description</label>
                <ReactQuill
                  value={formData.description}
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  onChange={(value) => handleQuillChange("description", value)}
                  className="h-[150px] mb-1"
                />
              </div>

              <div>
                <label className="block mt-12 mb-1 text-gray-700">Eligibility Criteria</label>
                <ReactQuill
                  value={formData.eligibilityCriteria}
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  onChange={(value) => handleQuillChange("eligibilityCriteria", value)}
                  className="h-[150px]"
                />
              </div>

              <div>
                <label className="block mt-12 mb-1 text-gray-700">Application Process</label>
                <ReactQuill
                  value={formData.applicationProcess}
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  onChange={(value) => handleQuillChange("applicationProcess", value)}
                  className="h-[150px]"
                />
              </div>

              <div className="mt-6">
                <label className="block mt-12 mb-1 text-sm font-medium text-gray-600">Contact Information</label>
                <ReactQuill
                  value={formData.contactInformation}
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  onChange={(value) => handleQuillChange("contactInformation", value)}
                  className="h-[150px]"
                />
              </div>

              <div className="mt-6">
                <label className="block mt-12 mb-1 text-sm font-medium text-gray-600">Supporting Documents</label>
                <div className="flex flex-col items-center justify-center mt-2 border-2 border-gray-300 border-dashed rounded-lg h-36 ">   {formData.files.length === 0 ? ( // Show "Choose File" if no files yet
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="px-4 py-2 text-[#92288C] rounded-md hover:underline"
                  >
                    Brows File
                  </button>
                ) : null}</div>
             
                {formData.files.map((file, index) => (
                  <div key={index} className="flex items-center space-x-4 mt-4">
                    <span className="w-3/4 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      className="px-4 py-2 text-white bg-red-500 rounded-md hover:bg-red-600"
                      onClick={() => handleRemoveFileField(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple  // Allow multiple file selection
                  onChange={handleFileChange}
                />
                {formData.files.length > 0 ? (
                  <button
                    type="button"
                    onClick={handleAddFileField}
                    className="mt-4 text-sm text-[#92288C] hover:underline"
                  >
                    Add more
                  </button>
                ) : null}
              </div>

              <div className="flex justify-end mt-6 space-x-4">
                <button
                  type="reset"
                  className="px-4 py-2 text-[#A8AAAE] bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </PageComponent></>
  );
}

export default AddLoan;